/**
 * useSEO — lightweight meta tag manager (no extra dependencies)
 *
 * Usage:
 *   useSEO({ title, description, canonical, image, type, noindex, jsonLd })
 */
import { useEffect } from 'react';

const SITE_NAME = 'BlueSpark Australia';
const BASE_URL = (process.env.REACT_APP_PUBLIC_URL || 'https://bluesparkautomotive.com.au').replace(/\/$/, '');
const DEFAULT_IMAGE = `${BASE_URL}/brand/bluespark-logo.png`;

function setMeta(name, content, attr = 'name') {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(data) {
  // Remove any prior structured-data blocks we added
  document
    .querySelectorAll('script[data-jsonld-managed="true"]')
    .forEach((el) => el.remove());
  if (!data) return;
  const blocks = Array.isArray(data) ? data : [data];
  blocks.forEach((block) => {
    if (!block) return;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.dataset.jsonldManaged = 'true';
    el.textContent = JSON.stringify(block);
    document.head.appendChild(el);
  });
}

function removeJsonLd() {
  document
    .querySelectorAll('script[data-jsonld-managed="true"]')
    .forEach((el) => el.remove());
}

export function useSEO({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd = null,
} = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const fullCanonical = canonical ? `${BASE_URL}${canonical}` : null;

    document.title = fullTitle;

    setMeta('description', description);
    setMeta('robots', noindex ? 'noindex,nofollow' : 'index,follow');

    setLink('canonical', fullCanonical);

    setMeta('og:title', fullTitle, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:image', image, 'property');
    setMeta('og:url', fullCanonical, 'property');
    setMeta('og:type', type, 'property');
    setMeta('og:site_name', SITE_NAME, 'property');

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);

    if (jsonLd) {
      setJsonLd(jsonLd);
    } else {
      removeJsonLd();
    }
  }, [title, description, canonical, image, type, noindex, jsonLd]);
}
